import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faCircleInfo, faSpinner, faTriangleExclamation, faWandMagicSparkles } from "@fortawesome/free-solid-svg-icons";
import styles from "../WorkoutAdminModal.module.css";
import { AI_GENERATING_MESSAGES, Z_AI_GENERATE_OVERLAY } from "./workoutAdminHelpers";
import { useWorkoutAdminContext } from "../../context/WorkoutAdminContext";

const aiFieldStyle = {
  width: "100%", boxSizing: "border-box",
  background: "var(--surface-secondary)",
  border: "1px solid rgba(255,255,255,0.14)",
  borderRadius: 8, padding: "8px 10px",
  color: "var(--text-primary)", fontSize: 13,
  colorScheme: "dark",
  outline: "none",
};

// Props removidas em favor de useWorkoutAdminContext() (issue 012,
// docs/07-issues.md) -- mesmo WorkoutAdminContext já usado por StudentsTab.jsx,
// StudentAssignmentModal.jsx e PlansTab.jsx.
export default function AIGenerateModal() {
  const {
    aiConfirming,
    aiForm,
    setAiForm,
    aiGenMsgIdx,
    aiGenerating,
    aiHasAdjusted,
    setAiHasAdjusted,
    aiPreview,
    setAiPreview,
    aiSuccess,
    setAiSuccess,
    handleConfirmGenerated,
    loadUserWorkoutPlans,
    selectedStudent,
    selectedStudentId,
    setAiConfirmOpen,
    setAiRationaleOpen,
    setShowAIModal,
    setStudentAssignments,
    studentAssignments,
  } = useWorkoutAdminContext();

  return createPortal(
        <div
          style={{
            position: "fixed", inset: 0, zIndex: Z_AI_GENERATE_OVERLAY,
            background: "rgba(10,20,40,0.7)", backdropFilter: "blur(8px)",
            display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto",
            paddingTop: "max(env(safe-area-inset-top, 20px), 20px)", paddingLeft: 20, paddingRight: 20, paddingBottom: 20,
          }}
          onClick={() => { if (!aiGenerating && !aiConfirming) { setShowAIModal(false); setAiSuccess(null); setAiPreview(null); setAiHasAdjusted(false); setAiRationaleOpen(false); } }}
        >
          <div
            style={{
              width: "min(720px,94vw)", background: "var(--surface-primary)",
              border: "1px solid var(--border-0-10)", borderRadius: 18,
              boxShadow: "0 24px 70px rgba(0,0,0,0.6)", padding: "24px 24px 20px",
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
              <FontAwesomeIcon icon={faWandMagicSparkles} style={{ color: "var(--feedback-success)", fontSize: 20 }} />
              <div>
                <h3 style={{ margin: 0, color: "var(--text-primary)", fontSize: 16 }}>Gerar treino com IA</h3>
                <p style={{ margin: 0, color: "var(--text-0-50)", fontSize: 12 }}>
                  {selectedStudent?.name}
                </p>
              </div>
            </div>

            {/* Perfil incompleto - bloqueia geração */}
            {(!selectedStudent?.gender || !selectedStudent?.birth_date) ? (
              <div style={{
                textAlign: "center", padding: "20px 0 10px",
                display: "flex", flexDirection: "column", alignItems: "center", gap: 12,
              }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 12,
                  background: "rgba(251,191,36,0.12)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "var(--feedback-warning)", fontSize: 20,
                }}>
                  <FontAwesomeIcon icon={faTriangleExclamation} />
                </div>
                <p style={{ margin: 0, color: "var(--text-primary)", fontWeight: 600, fontSize: 14 }}>
                  Perfil incompleto
                </p>
                <p style={{ margin: 0, color: "var(--text-0-50)", fontSize: 13, lineHeight: 1.5 }}>
                  Para gerar um treino personalizado com IA, o aluno precisa ter{" "}
                  <strong style={{ color: "var(--text-primary)" }}>gênero</strong> e{" "}
                  <strong style={{ color: "var(--text-primary)" }}>data de nascimento</strong> preenchidos.
                  <br />Peça ao aluno que complete o perfil no app.
                </p>
                <button
                  style={{
                    marginTop: 4,
                    background: "var(--border-0-08)", color: "var(--text-primary)",
                    border: "1px solid var(--border-0-10)", borderRadius: 10,
                    padding: "9px 20px", fontWeight: 600, fontSize: 13, cursor: "pointer",
                  }}
                  onClick={() => { if (!aiGenerating) setShowAIModal(false); }}
                >
                  Fechar
                </button>
              </div>
            ) : aiSuccess ? (
              /* Preview pós-geração */
              <div style={{ display: "flex", flexDirection: "column", gap: 14, maxHeight: "60vh", overflowY: "auto", paddingRight: 4 }}>

                {/* Header de sucesso */}
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <FontAwesomeIcon icon={faCheck} style={{ color: "var(--feedback-success)", fontSize: 20 }} />
                  <div>
                    <p style={{ color: "var(--text-primary)", fontWeight: 700, margin: 0, fontSize: 15 }}>
                      {aiSuccess.days_created} treino(s) criado(s) para {selectedStudent?.name}
                    </p>
                    <p style={{ color: "var(--text-0-50)", fontSize: 12, margin: 0 }}>
                      Plano vinculado e ativo · gerado agora
                    </p>
                  </div>
                </div>

                {/* Racionalização */}
                {aiSuccess.rationale && (
                  <div style={{
                    background: "rgba(74,222,128,0.06)",
                    border: "1px solid rgba(74,222,128,0.18)",
                    borderRadius: 10,
                    padding: "12px 14px",
                  }}>
                    <p style={{ color: "var(--feedback-success)", fontWeight: 700, fontSize: "var(--fs-xs)", margin: "0 0 6px", textTransform: "uppercase", letterSpacing: ".6px" }}>
                      Racionalização da IA
                    </p>
                    <p style={{ color: "var(--text-0-70)", fontSize: 12, margin: 0, lineHeight: 1.65, whiteSpace: "pre-wrap" }}>
                      {aiSuccess.rationale}
                    </p>
                  </div>
                )}

                {/* Resumo dos dias */}
                {aiSuccess.days && aiSuccess.days.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <p style={{ color: "var(--text-0-50)", fontSize: "var(--fs-xs)", margin: 0, textTransform: "uppercase", letterSpacing: ".6px" }}>
                      Resumo do plano
                    </p>
                    {aiSuccess.days.map((day, i) => (
                      <div key={i} style={{
                        background: "rgba(255,255,255,0.04)",
                        border: "1px solid var(--border-0-08)",
                        borderRadius: 8,
                        padding: "10px 12px",
                      }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                          <span style={{ color: "var(--text-primary)", fontWeight: 700, fontSize: 13 }}>{day.name}</span>
                          <span style={{ color: "var(--text-0-40)", fontSize: "var(--fs-xs)" }}>{day.suggested_day} · {day.exercises?.length || 0} exercícios</span>
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {(day.exercises || []).map((ex, j) => (
                            <span key={j} style={{
                              background: "var(--border-0-06)",
                              borderRadius: 4,
                              padding: "2px 7px",
                              fontSize: "var(--fs-xs)",
                              color: "var(--text-0-60)",
                            }}>
                              {ex.exercise_name || `Ex #${ex.exercise_item_id}`}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Aviso de ajuste manual */}
                <div style={{
                  background: "rgba(251,191,36,0.08)",
                  border: "1px solid rgba(251,191,36,0.22)",
                  borderRadius: 8,
                  padding: "10px 13px",
                  display: "flex",
                  gap: 8,
                  alignItems: "flex-start",
                }}>
                  <FontAwesomeIcon icon={faCircleInfo} style={{ color: "var(--feedback-warning)", fontSize: 13, marginTop: 2, flexShrink: 0 }} />
                  <p style={{ color: "var(--text-0-70)", fontSize: 12, margin: 0, lineHeight: 1.55 }}>
                    Caso queira fazer ajustes pontuais, acesse o plano do aluno e edite manualmente os exercícios, séries ou dias.
                  </p>
                </div>

                {/* Ação */}
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  <button
                    style={{
                      background: "var(--border-0-06)", color: "var(--text-primary)",
                      border: "1px solid var(--border-0-10)", borderRadius: 10,
                      padding: "9px 16px", fontWeight: 600, fontSize: 13, cursor: "pointer",
                    }}
                    onClick={() => { setShowAIModal(false); setAiSuccess(null); setAiPreview(null); setAiHasAdjusted(false); setAiRationaleOpen(false); }}
                  >
                    Fechar
                  </button>
                  <button
                    style={{
                      background: "var(--accent)", color: "var(--on-accent)", border: "none", borderRadius: 10,
                      padding: "9px 20px", fontWeight: 700, fontSize: 13, cursor: "pointer",
                    }}
                    onClick={async () => {
                      setShowAIModal(false);
                      setAiSuccess(null); setAiPreview(null); setAiHasAdjusted(false); setAiRationaleOpen(false);
                      const assignments = await loadUserWorkoutPlans(selectedStudentId);
                      setStudentAssignments(assignments || []);
                    }}
                  >
                    Ver treinos do aluno
                  </button>
                </div>
              </div>
            ) : aiPreview ? (
              /* Prévia pós-geração — ainda NÃO persistida. Espelha a fase "preview" do
                 SelfWorkoutModal: o professor pode "← Ajustar" (volta ao form com os
                 mesmos dados) e gerar de novo, ou "Confirmar" pra persistir de fato.
                 Ver docs/07-issues.md. */
              <div style={{ display: "flex", flexDirection: "column", gap: 14, maxHeight: "60vh", overflowY: "auto", paddingRight: 4 }}>

                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <FontAwesomeIcon icon={faWandMagicSparkles} style={{ color: "var(--feedback-success)", fontSize: 20 }} />
                  <div>
                    <p style={{ color: "var(--text-primary)", fontWeight: 700, margin: 0, fontSize: 15 }}>
                      Prévia do treino para {selectedStudent?.name}
                    </p>
                    <p style={{ color: "var(--text-0-50)", fontSize: 12, margin: 0 }}>
                      Revise antes de salvar - nada foi criado ainda
                    </p>
                  </div>
                </div>

                {aiPreview.rationale && (
                  <div style={{
                    background: "rgba(74,222,128,0.06)",
                    border: "1px solid rgba(74,222,128,0.18)",
                    borderRadius: 10,
                    padding: "12px 14px",
                  }}>
                    <p style={{ color: "var(--feedback-success)", fontWeight: 700, fontSize: "var(--fs-xs)", margin: "0 0 6px", textTransform: "uppercase", letterSpacing: ".6px" }}>
                      Racionalização da IA
                    </p>
                    <p style={{ color: "var(--text-0-70)", fontSize: 12, margin: 0, lineHeight: 1.65, whiteSpace: "pre-wrap" }}>
                      {aiPreview.rationale}
                    </p>
                  </div>
                )}

                {aiPreview.days && aiPreview.days.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <p style={{ color: "var(--text-0-50)", fontSize: "var(--fs-xs)", margin: 0, textTransform: "uppercase", letterSpacing: ".6px" }}>
                      Resumo do plano
                    </p>
                    {aiPreview.days.map((day, i) => (
                      <div key={i} style={{
                        background: "rgba(255,255,255,0.04)",
                        border: "1px solid var(--border-0-08)",
                        borderRadius: 8,
                        padding: "10px 12px",
                      }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                          <span style={{ color: "var(--text-primary)", fontWeight: 700, fontSize: 13 }}>{day.name}</span>
                          <span style={{ color: "var(--text-0-40)", fontSize: "var(--fs-xs)" }}>{day.suggested_day} · {day.exercises?.length || 0} exercícios</span>
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {(day.exercises || []).map((ex, j) => (
                            <span key={j} style={{
                              background: "var(--border-0-06)",
                              borderRadius: 4,
                              padding: "2px 7px",
                              fontSize: "var(--fs-xs)",
                              color: "var(--text-0-60)",
                            }}>
                              {ex.exercise_name || `Ex #${ex.exercise_item_id}`}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {studentAssignments.length > 0 && (
                  <div style={{
                    background: "rgba(251,191,36,0.08)",
                    border: "1px solid rgba(251,191,36,0.22)",
                    borderRadius: 8,
                    padding: "10px 13px",
                    display: "flex",
                    gap: 8,
                    alignItems: "flex-start",
                  }}>
                    <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: "var(--feedback-warning)", fontSize: 13, marginTop: 2, flexShrink: 0 }} />
                    <p style={{ color: "var(--text-0-70)", fontSize: 12, margin: 0, lineHeight: 1.55 }}>
                      Ao confirmar, os {studentAssignments.length} treino(s) existente(s) de {selectedStudent?.name} serão substituídos por este plano.
                    </p>
                  </div>
                )}

                {/* Aviso de ajuste manual — mesma reassurance que aparecia na tela final
                    antes do fluxo de prévia existir; reforçada aqui pra quem confirma
                    sem reabrir a tela de sucesso depois. */}
                <div style={{
                  background: "rgba(251,191,36,0.08)",
                  border: "1px solid rgba(251,191,36,0.22)",
                  borderRadius: 8,
                  padding: "10px 13px",
                  display: "flex",
                  gap: 8,
                  alignItems: "flex-start",
                }}>
                  <FontAwesomeIcon icon={faCircleInfo} style={{ color: "var(--feedback-warning)", fontSize: 13, marginTop: 2, flexShrink: 0 }} />
                  <p style={{ color: "var(--text-0-70)", fontSize: 12, margin: 0, lineHeight: 1.55 }}>
                    Caso queira fazer ajustes pontuais depois de salvar, acesse o plano do aluno e edite manualmente os exercícios, séries ou dias.
                  </p>
                </div>

                <div style={{ display: "flex", gap: 10, justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    <button
                      type="button"
                      style={{
                        background: "var(--border-0-06)", color: "var(--text-primary)",
                        border: "1px solid var(--border-0-10)", borderRadius: 10,
                        padding: "9px 16px", fontWeight: 600, fontSize: 13,
                        cursor: (aiConfirming || aiHasAdjusted) ? "not-allowed" : "pointer",
                        opacity: (aiConfirming || aiHasAdjusted) ? 0.5 : 1,
                      }}
                      disabled={aiConfirming || aiHasAdjusted}
                      title={aiHasAdjusted ? "Você já ajustou uma vez" : undefined}
                      onClick={() => { setAiHasAdjusted(true); setAiPreview(null); setAiRationaleOpen(true); }}
                    >
                      ← Ajustar
                    </button>
                    {aiHasAdjusted && (
                      <span style={{ color: "var(--text-0-40)", fontSize: "var(--fs-xs)" }}>
                        Ajuste permitido apenas 1× por geração
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    style={{
                      background: aiConfirming ? "rgba(var(--accent-rgb),0.4)" : "var(--accent)",
                      color: "var(--on-accent)", border: "none", borderRadius: 10,
                      padding: "9px 20px", fontWeight: 700, fontSize: 13,
                      cursor: aiConfirming ? "not-allowed" : "pointer",
                      display: "flex", alignItems: "center", gap: 8,
                    }}
                    disabled={aiConfirming}
                    onClick={handleConfirmGenerated}
                  >
                    {aiConfirming
                      ? <><FontAwesomeIcon icon={faSpinner} spin /> Salvando...</>
                      : <><FontAwesomeIcon icon={faCheck} /> Confirmar e criar treinos</>}
                  </button>
                </div>
              </div>
            ) : aiGenerating ? (
              /* Tela de geração - substitui o formulário enquanto a IA trabalha */
              <div style={{
                display: "flex", flexDirection: "column", alignItems: "center",
                justifyContent: "center", gap: 16, padding: "32px 16px",
                minHeight: 220,
              }}>
                <div style={{
                  width: 56, height: 56, borderRadius: 16,
                  background: "var(--accent-0-12)", border: "1px solid rgba(var(--accent-rgb),0.25)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <FontAwesomeIcon icon={faSpinner} spin style={{ color: "var(--feedback-success)", fontSize: 24 }} />
                </div>
                <div style={{ textAlign: "center" }}>
                  <p style={{ margin: "0 0 6px", color: "var(--text-primary)", fontWeight: 700, fontSize: 15 }}>
                    Gerando treino para {selectedStudent?.name}…
                  </p>
                  <p
                    key={aiGenMsgIdx}
                    className={styles.aiGenMsg}
                    style={{ margin: "0 0 6px", color: "var(--text-0-60)", fontSize: 13, fontWeight: 600 }}
                  >
                    {AI_GENERATING_MESSAGES[aiGenMsgIdx]}
                  </p>
                  <p style={{ margin: 0, color: "var(--text-0-50)", fontSize: 13 }}>
                    Por favor, aguarde sem fechar esta janela.
                  </p>
                </div>
              </div>
            ) : (
              /* Formulário */
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

                {/* Aviso de substituição quando o aluno já tem treinos */}
                {studentAssignments.length > 0 && (
                  <div style={{
                    background: "var(--border-0-10)",
                    border: "1px solid rgba(251,191,36,0.35)",
                    borderRadius: 8,
                    padding: "10px 14px",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    fontSize: 12,
                    color: "var(--feedback-warning)",
                  }}>
                    <span style={{ fontSize: 16, lineHeight: 1 }}><FontAwesomeIcon icon={faTriangleExclamation} /></span>
                    <span>
                      <strong>Os {studentAssignments.length} treino(s) existente(s) serão excluídos</strong> e substituídos pelos novos gerados pela IA.
                      O histórico de alterações será salvo automaticamente.
                    </span>
                  </div>
                )}

                {/* Objetivo */}
                <div>
                  <label style={{ color: "var(--text-0-70)", fontSize: 12, display: "block", marginBottom: 4 }}>
                    Objetivo
                  </label>
                  <select
                    style={aiFieldStyle}
                    value={aiForm.goals[0]}
                    onChange={e => setAiForm(f => ({ ...f, goals: [e.target.value] }))}
                  >
                    <option value="hipertrofia">Hipertrofia</option>
                    <option value="força">Força</option>
                    <option value="emagrecimento">Emagrecimento</option>
                    <option value="condicionamento">Condicionamento</option>
                    <option value="reabilitação">Reabilitação</option>
                  </select>
                </div>

                {/* Dias/semana */}
                <div>
                  <label style={{ color: "var(--text-0-70)", fontSize: 12, display: "block", marginBottom: 4 }}>
                    Dias/semana
                  </label>
                  <select
                    style={aiFieldStyle}
                    value={aiForm.days_per_week}
                    onChange={e => setAiForm(f => ({ ...f, days_per_week: Number(e.target.value) }))}
                  >
                    {[2,3,4,5,6].map(n => <option key={n} value={n}>{n}x</option>)}
                  </select>
                </div>

                {/* Duração + Foco */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <div>
                    <label style={{ color: "var(--text-0-70)", fontSize: 12, display: "block", marginBottom: 4 }}>
                      Duração (min)
                    </label>
                    <select
                      style={aiFieldStyle}
                      value={aiForm.session_duration_minutes}
                      onChange={e => setAiForm(f => ({ ...f, session_duration_minutes: Number(e.target.value) }))}
                    >
                      <option value={45}>45 min</option>
                      <option value={60}>60 min</option>
                      <option value={75}>75 min</option>
                      <option value={90}>90 min</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ color: "var(--text-0-70)", fontSize: 12, display: "block", marginBottom: 4 }}>
                      Foco muscular
                    </label>
                    <select
                      style={aiFieldStyle}
                      value={aiForm.focus}
                      onChange={e => setAiForm(f => ({ ...f, focus: e.target.value }))}
                    >
                      <option value="">Equilibrado</option>
                      <option value="superiores">Superiores</option>
                      <option value="inferiores">Inferiores</option>
                      <option value="glúteos">Glúteos</option>
                      <option value="core">Core</option>
                      <option value="lombar">Lombar</option>
                      <option value="obliquos">Oblíquos</option>
                    </select>
                  </div>
                </div>

                {/* Restrições */}
                <div>
                  <label style={{ color: "var(--text-0-70)", fontSize: 12, display: "block", marginBottom: 4 }}>
                    Restrições / observações <span style={{ opacity: 0.5 }}>(opcional)</span>
                  </label>
                  <input
                    style={aiFieldStyle}
                    type="text"
                    placeholder="Ex: dor no joelho, evitar agachamento..."
                    value={aiForm.restrictions}
                    onChange={e => setAiForm(f => ({ ...f, restrictions: e.target.value }))}
                  />
                </div>

                {/* Notas técnicas do professor */}
                <div>
                  <label style={{ color: "var(--text-0-70)", fontSize: 12, display: "block", marginBottom: 8 }}>
                    Instruções técnicas <span style={{ opacity: 0.5 }}>(opcional)</span>
                  </label>
                  <textarea
                    style={{ ...aiFieldStyle, resize: "vertical", minHeight: 100, lineHeight: 1.5 }}
                    placeholder={"Ex: Priorizar 5-6 séries nos compostos de peito e costas, RIR 2-3. Focar em ombro, trapézio e posterior de coxa. Evitar supino inclinado (tendinite no ombro esq. - trocar por halteres). Sem polia/cross disponível. Descanso 90s entre séries."}
                    value={aiForm.teacher_notes}
                    onChange={e => setAiForm(f => ({ ...f, teacher_notes: e.target.value }))}
                    aria-label="Instruções técnicas do professor"
                  />
                  <div style={{
                    marginTop: 8, padding: "8px 12px", borderRadius: 10,
                    background: "rgba(var(--accent-rgb),0.15)",
                    border: "1px solid rgba(var(--accent-rgb),0.35)",
                    display: "flex", alignItems: "flex-start", gap: 8,
                  }}>
                    <span style={{ color: "var(--feedback-success)", fontSize: 13, lineHeight: 1, marginTop: 1 }}><FontAwesomeIcon icon={faCircleInfo} /></span>
                    <p style={{ margin: 0, fontSize: 12, color: "var(--feedback-success)", lineHeight: 1.5 }}>
                      Contexto técnico exclusivo do professor. A IA <strong>prioriza</strong> instruções específicas - número de séries, foco muscular, exercícios a incluir/evitar, RIR, descanso - dentro dos limites de tempo de sessão e volume semanal seguro. Quando um pedido não couber nesses limites, isso vem explicado no rationale técnico.
                    </p>
                  </div>
                </div>

                {/* Objetivos secundários */}
                <div>
                  <label style={{ color: "var(--text-0-70)", fontSize: 12, display: "block", marginBottom: 8 }}>
                    Objetivos adicionais <span style={{ opacity: 0.5 }}>(opcional)</span>
                  </label>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {[
                      "Melhorar postura",
                      "Prevenir lesões",
                      "Ganhar flexibilidade",
                      "Aumentar energia",
                    ].map(goal => {
                      const active = aiForm.secondary_goals.includes(goal);
                      return (
                        <button
                          key={goal}
                          type="button"
                          onClick={() => setAiForm(f => ({
                            ...f,
                            secondary_goals: active
                              ? f.secondary_goals.filter(g => g !== goal)
                              : [...f.secondary_goals, goal],
                          }))}
                          style={{
                            padding: "5px 12px",
                            borderRadius: 20,
                            fontSize: 12,
                            fontWeight: 500,
                            cursor: "pointer",
                            border: active ? "1px solid #4ade80" : "1px solid rgba(255,255,255,0.15)",
                            background: active ? "rgba(74,222,128,0.12)" : "var(--border-0-04)",
                            color: active ? "#4ade80" : "var(--text-0-60)",
                            transition: "all 0.15s",
                          }}
                        >
                          {goal}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Equipamentos */}
                <div>
                  <label style={{ color: "var(--text-0-70)", fontSize: 12, display: "block", marginBottom: 8 }}>
                    Equipamentos disponíveis <span style={{ opacity: 0.5 }}>(vazio = academia completa)</span>
                  </label>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {[
                      { v: "barra",      l: "Barra livre" },
                      { v: "halteres",   l: "Halteres" },
                      { v: "maquina",    l: "Máquinas" },
                      { v: "cross",      l: "Cabo/Cross" },
                      { v: "barra_fixa", l: "Barra fixa" },
                      { v: "miniband",   l: "Miniband" },
                    ].map(({ v, l }) => {
                      const sel = aiForm.equipment.includes(v);
                      return (
                        <button key={v} type="button"
                          onClick={() => setAiForm(f => ({
                            ...f,
                            equipment: sel ? f.equipment.filter(e => e !== v) : [...f.equipment, v],
                          }))}
                          style={{
                            padding: "6px 14px", borderRadius: 20, fontSize: 12, cursor: "pointer",
                            border: sel ? "1.5px solid var(--accent)" : "1.5px solid var(--border-0-10)",
                            background: sel ? "rgba(var(--accent-rgb),0.15)" : "var(--border-0-06)",
                            color: sel ? "var(--accent)" : "var(--text-0-70)",
                            transition: "all 0.15s",
                          }}>
                          {l}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Cardio */}
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: aiForm.include_cardio ? 10 : 0 }}>
                    <button
                      type="button"
                      onClick={() => setAiForm(f => ({ ...f, include_cardio: !f.include_cardio }))}
                      style={{
                        width: 36, height: 20, borderRadius: 10, border: "none", cursor: "pointer",
                        background: aiForm.include_cardio ? "var(--accent)" : "var(--border-0-10)",
                        position: "relative", flexShrink: 0, transition: "background 0.2s",
                      }}
                    >
                      <span style={{
                        position: "absolute", top: 2,
                        left: aiForm.include_cardio ? 18 : 2,
                        width: 16, height: 16, borderRadius: "50%",
                        background: "var(--text-primary)",
                        transition: "left 0.2s",
                      }} />
                    </button>
                    <label
                      style={{ color: "var(--text-0-70)", fontSize: 12, cursor: "pointer" }}
                      onClick={() => setAiForm(f => ({ ...f, include_cardio: !f.include_cardio }))}
                    >
                      Incluir cardio no final de cada sessão
                    </label>
                  </div>
                  {aiForm.include_cardio && (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      <div>
                        <label style={{ color: "var(--text-0-70)", fontSize: 12, display: "block", marginBottom: 4 }}>
                          Tipo de cardio
                        </label>
                        <select
                          style={aiFieldStyle}
                          value={aiForm.cardio_type}
                          onChange={e => setAiForm(f => ({ ...f, cardio_type: e.target.value }))}
                        >

                          <option value="livre">Livre</option>
                          <option value="Esteira">Esteira</option>
                          <option value="Bicicleta">Bicicleta</option>
                          <option value="Elíptico">Elíptico</option>
                        </select>
                      </div>
                      <div>
                        <label style={{ color: "var(--text-0-70)", fontSize: 12, display: "block", marginBottom: 4 }}>
                          Duração (min)
                        </label>
                        <select
                          style={aiFieldStyle}
                          value={aiForm.cardio_duration_minutes}
                          onChange={e => setAiForm(f => ({ ...f, cardio_duration_minutes: Number(e.target.value) }))}
                        >
                          <option value={15}>15 min</option>
                          <option value={20}>20 min</option>
                          <option value={30}>30 min</option>
                          <option value={45}>45 min</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>

                {/* Aviso */}
                {!aiGenerating && (
                  <p style={{ margin: 0, fontSize: "var(--fs-xs)", color: "var(--text-0-40)", lineHeight: 1.5 }}>
                    A IA criará {aiForm.days_per_week} treino(s) vinculados a {selectedStudent?.name} usando
                    os exercícios cadastrados na plataforma.
                  </p>
                )}

                {/* Streaming preview */}
                {/* Ações */}
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
                  <button
                    style={{
                      background: "var(--border-0-08)", color: "var(--text-primary)",
                      border: "1px solid var(--border-0-10)", borderRadius: 10,
                      padding: "9px 16px", fontWeight: 600, fontSize: 14, cursor: "pointer",
                    }}
                    onClick={() => setShowAIModal(false)}
                    disabled={aiGenerating}
                  >
                    Cancelar
                  </button>
                  <button
                    style={{
                      background: aiGenerating ? "rgba(var(--accent-rgb),0.4)" : "var(--accent)",
                      color: "var(--on-accent)", border: "none", borderRadius: 10,
                      padding: "9px 20px", fontWeight: 700, fontSize: 14,
                      cursor: aiGenerating ? "not-allowed" : "pointer",
                      display: "flex", alignItems: "center", gap: 8,
                      transition: "background .15s",
                    }}
                    disabled={aiGenerating}
                    onClick={() => setAiConfirmOpen(true)}
                  >
                    {aiGenerating
                      ? "Gerando…"
                      : <><FontAwesomeIcon icon={faWandMagicSparkles} /> Gerar treino</>
                    }
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )
}
